import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ChatInterface, SchedulingChatResponse } from './chat.interface';
import { BotService } from '../../core/services/bot.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ResponseGetBotInterface } from '../../models/bot.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { GlobalObservableService } from 'src/app/core/services/global-observable.service';
import { LoggerService } from 'src/app/core/services/logger.service';
import { SchedulingService } from 'src/app/core/services/scheduling.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export type ExperimentalVariant = 'A' | 'B' | 'C';

@Component({
  selector: 'globot-widget-conf',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnInit, AfterViewChecked, OnDestroy {
  private destroy$ = new Subject<void>();
  @ViewChild('scrollMe') myScrollContainer!: ElementRef;
  @ViewChild('chatContainer') chatContainer!: ElementRef;

  haveError: boolean = false;
  subscriptionLimitReached: boolean = false;

  currentQuestion: string = '';
  previousQuestion: ChatInterface | null = null;
  botId: string = '';
  chatHistory: ChatInterface[] = [];
  onTransaction: boolean = false;

  bot: ResponseGetBotInterface | undefined;
  origin: string | undefined;

  // Scheduling mode (legacy)
  schedulingMode: boolean = false;
  correlationId: string = '';
  schedulingEnabled: boolean = false;

  // Experimental variant
  experimentalVariant: ExperimentalVariant | null = null;

  experimentalVariantOptions = [
    { label: 'Legacy', value: null },
    { label: 'A — Supervisor', value: 'A' },
    { label: 'B — Handoff', value: 'B' },
    { label: 'C — Bidireccional', value: 'C' },
  ];

  constructor(
    private readonly router: Router,
    private readonly cd: ChangeDetectorRef,
    private readonly botService: BotService,
    private readonly schedulingService: SchedulingService,
    private readonly aRoute: ActivatedRoute,
    private globalService: GlobalObservableService,
    private logger: LoggerService
  ) {
    this.botId = this.aRoute.snapshot.paramMap.get('token') ?? '';
    this.origin = this.aRoute.snapshot.queryParamMap.get('o') ?? window.location.origin;

    if (this.botId.length === 0) {
      const sections = this.router.routerState.snapshot.url.split('/');
      this.botId = sections[3];
    }
    this.getBotInfo();
  }

  ngAfterViewChecked(): void {
    this.scrollToBottom(0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.globalService
      .getObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((message) => {
        if (message.type === 'botName') {
          this.bot!.name = message.data.name;
        }
      });

    const storageKey = this.getStorageKey();

    // Carga el historial del chat desde el almacenamiento local
    const storedChatHistory = localStorage.getItem(storageKey);
    if (storedChatHistory) {
      try {
        this.chatHistory = JSON.parse(storedChatHistory);
      } catch (error) {
        this.logger.error('Error parsing chat history', error);
        localStorage.removeItem(storageKey);
      }
    }

    // Restore correlation id (single per session — not reset on mode toggle)
    const storedCorrelationId = localStorage.getItem(`schedulingCorrelationId_${this.botId}`);
    if (storedCorrelationId) {
      this.correlationId = storedCorrelationId;
    }

    // Restore experimental variant
    const storedVariant = localStorage.getItem(`experimentalVariant_${this.botId}`);
    if (storedVariant && storedVariant !== 'null' && ['A', 'B', 'C'].includes(storedVariant)) {
      this.experimentalVariant = storedVariant as ExperimentalVariant;
    }

    // Check if scheduling is enabled for this bot
    this.schedulingService.refreshSchedulingEnabled(this.botId);
    this.schedulingService.schedulingEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.schedulingEnabled = enabled;
        if (!enabled) {
          this.schedulingMode = false;
        }
      });

    this.scrollToBottom(0);
  }

  scrollToBottom(_h: number): void {
    try {
      this.myScrollContainer.nativeElement.scrollTop =
        this.chatContainer.nativeElement.scrollHeight;
    } catch (err) {}
    this.cd.detectChanges();
  }

  toggleSchedulingMode(): void {
    this.schedulingMode = !this.schedulingMode;
    // Bug fix: do NOT reset correlation_id on toggle. Only "Nueva conversación" resets it.
  }

  setExperimentalVariant(variant: ExperimentalVariant | null): void {
    this.experimentalVariant = variant;
    localStorage.setItem(`experimentalVariant_${this.botId}`, variant ?? 'null');
    // Intentionally NOT resetting correlation_id — same session continues.
  }

  private ensureCorrelationId(): void {
    if (!this.correlationId) {
      this.correlationId = crypto.randomUUID();
      localStorage.setItem(`schedulingCorrelationId_${this.botId}`, this.correlationId);
    }
  }

  private resetSchedulingConversation(): void {
    this.correlationId = crypto.randomUUID();
    localStorage.setItem(`schedulingCorrelationId_${this.botId}`, this.correlationId);
  }

  sendQuestion() {
    const storageKey = this.getStorageKey();
    if (this.currentQuestion.length === 0) return;
    if (this.onTransaction) return;

    if (!this.haveError) {
      this.previousQuestion = { message: this.currentQuestion, type: 'human', date: new Date() };
    }

    this.haveError = false;
    const q: ChatInterface = { message: this.currentQuestion, type: 'human', date: new Date() };
    this.chatHistory.push(q);
    this.currentQuestion = '';

    const loading: ChatInterface = { message: '...', type: 'loading', date: new Date() };
    this.chatHistory.push(loading);
    this.onTransaction = true;

    if (this.experimentalVariant) {
      this.sendExperimentalQuestion(q.message, storageKey, this.experimentalVariant);
    } else if (this.schedulingMode) {
      this.sendSchedulingQuestion(q.message, storageKey);
    } else {
      this.sendNormalQuestion(q.message, storageKey);
    }
  }

  private sendNormalQuestion(message: string, storageKey: string): void {
    this.botService
      .sendMessage({ prompt: message }, this.bot?.token!, this.origin)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value: { response: string }) => {
          const q: ChatInterface = { message: value?.response, type: 'bot', date: new Date() };
          this.chatHistory.pop();
          this.chatHistory.push(q);
          localStorage.setItem(storageKey, JSON.stringify(this.chatHistory));
        },
        complete: () => {
          this.onTransaction = false;
          this.cd.detectChanges();
        },
        error: (err: HttpErrorResponse) => {
          this.onTransaction = false;
          this.cd.detectChanges();
          if (err.error && err.error.code === 4) {
            this.subscriptionLimitReached = true;
          } else {
            this.haveError = true;
          }
          this.chatHistory.pop();
        },
      });
  }

  private sendSchedulingQuestion(message: string, storageKey: string): void {
    this.ensureCorrelationId();

    this.schedulingService
      .sendSchedulingMessage(this.bot?.token!, {
        message,
        correlation_id: this.correlationId,
        channel: 'WEB',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value: SchedulingChatResponse) => {
          const botMsg: ChatInterface = {
            message: value.response,
            type: 'bot',
            date: new Date(),
            scheduling: {
              intent: value.intent,
              action_taken: value.action_taken,
              success: value.success,
              awaiting_input: value.awaiting_input,
              available_slots: value.available_slots,
              appointment: value.appointment,
              error_code: value.error_code,
              error_message: value.error_message,
            },
          };
          this.chatHistory.pop();
          this.chatHistory.push(botMsg);
          localStorage.setItem(storageKey, JSON.stringify(this.chatHistory));
        },
        complete: () => {
          this.onTransaction = false;
          this.cd.detectChanges();
        },
        error: (err: HttpErrorResponse) => {
          this.onTransaction = false;
          this.cd.detectChanges();
          this.haveError = true;
          this.chatHistory.pop();
        },
      });
  }

  private sendExperimentalQuestion(
    message: string,
    storageKey: string,
    variant: ExperimentalVariant
  ): void {
    this.ensureCorrelationId();

    const payload = {
      prompt: message,
      origin: this.origin,
      sender_id: this.correlationId,
      sender_name: '',
    };

    this.botService
      .sendExperimentalMessage(payload, this.bot?.token!, this.correlationId, variant)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value: { response: string; chatbot_history_id?: string }) => {
          const q: ChatInterface = { message: value.response, type: 'bot', date: new Date() };
          this.chatHistory.pop();
          this.chatHistory.push(q);
          localStorage.setItem(storageKey, JSON.stringify(this.chatHistory));
        },
        complete: () => {
          this.onTransaction = false;
          this.cd.detectChanges();
        },
        error: (err: HttpErrorResponse) => {
          this.onTransaction = false;
          this.cd.detectChanges();
          if (err.error && err.error.code === 4) {
            this.subscriptionLimitReached = true;
          } else {
            this.haveError = true;
          }
          this.chatHistory.pop();
        },
      });
  }

  getBotInfo() {
    this.botService
      .getBot(this.botId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value) => {
          this.bot = value;
          localStorage.setItem('bot', JSON.stringify(value));
          this.verifyWelcomeMessage();
        },
      });
  }

  reset() {
    if (this.previousQuestion) {
      this.sendPreviousQuestion();
      return;
    }

    this.onTransaction = false;
    this.cd.detectChanges();
    this.previousQuestion = null;
  }

  sendPreviousQuestion() {
    this.haveError = false;
    const prevQuestion: ChatInterface = this.previousQuestion!;
    const loading: ChatInterface = { message: '...', type: 'loading', date: new Date() };
    this.chatHistory.push(loading);
    const storageKey = this.getStorageKey();

    if (this.experimentalVariant) {
      this.onTransaction = true;
      this.sendExperimentalQuestion(prevQuestion.message, storageKey, this.experimentalVariant);
    } else if (this.schedulingMode) {
      this.onTransaction = true;
      this.sendSchedulingQuestion(prevQuestion.message, storageKey);
    } else {
      this.botService
        .sendMessage({ prompt: prevQuestion.message }, this.bot?.token!, this.origin)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (value: { response: string }) => {
            const q: ChatInterface = {
              message: value?.response,
              type: 'bot',
              date: new Date(),
            };
            this.chatHistory.pop();
            this.chatHistory.push(q);
          },
          complete: () => {
            this.haveError = false;
            this.onTransaction = false;
            this.cd.detectChanges();
          },
          error: (_err) => {
            this.onTransaction = false;
            this.cd.detectChanges();
            this.haveError = true;
            this.chatHistory.pop();
          },
        });
    }
  }

  private verifyWelcomeMessage() {
    if (!this.bot?.enabled_welcome_message || this.bot.welcome_message.length === 0) return;
    const messageWelcome: ChatInterface = {
      message: this.bot.welcome_message,
      type: 'bot',
      date: new Date(),
    };
    this.chatHistory.push(messageWelcome);
  }

  trackByIndex(index: number): number {
    return index;
  }

  clearChat() {
    this.chatHistory = [];

    const storageKey = this.getStorageKey();
    localStorage.removeItem(storageKey);

    this.haveError = false;
    this.onTransaction = false;
    this.previousQuestion = null;

    // Reset correlation_id only on explicit "Nueva conversación"
    this.resetSchedulingConversation();

    this.cd.detectChanges();
  }

  private getStorageKey(): string {
    if (this.experimentalVariant) {
      return `chatHistory_${this.botId}_exp${this.experimentalVariant}`;
    }
    const suffix = this.schedulingMode ? '_scheduling' : '';
    return `chatHistory_${this.botId}${suffix}`;
  }
}
